import React, { useState, useEffect } from 'react';
import { SideMenu, Header, TopMenu } from 'maxcu-shared-lib';
import { fetchGetPointsData, submitCodeRedemption, submitSalesforceCase } from '../../services/api';
import '../../styles/output.css';
import '../../styles/index.css';
import menuItems from '../SideMenu/menuItems';

const REQUEST_OPTIONS = [
  {
    value: 'financial_education',
    label: 'Financial Education',
    requiresUpload: true,
    case_type: 'member_request_youth_rewards',
    subject: 'Financial Education Points Request',
  },
  {
    value: 'report_card_rewards',
    label: 'Report Card Rewards',
    requiresUpload: true,
    case_type: 'member_request_youth_rewards',
    subject: 'Report Card Rewards Points Request',
  },
];

function GetPoints() {
  const [pointsData, setPointsData] = useState(null);
  const [memberNumber, setMemberNumber] = useState('');
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Tile 1 — code entry
  const [code, setCode] = useState('');
  const [codeResult, setCodeResult] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);

  // Tile 2 — request points
  const [selectedOption, setSelectedOption] = useState('');
  const [caseFile, setCaseFile] = useState(null);
  const [caseResult, setCaseResult] = useState(null);
  const [caseLoading, setCaseLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    document.title = "Bonus Points | MAX Credit Union";

    const getData = async () => {
      try {
        const result = await fetchGetPointsData();
        setTimeout(() => {
          if (isMounted) {
            setPointsData(result);
            setMemberNumber(result?.member_number || '');
          }
        }, 1000);
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      }
    };
    getData();

    const handleResize = () => {
      if (window.innerWidth < 600) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleCopyCode = () => {
    if (pointsData?.referral_code) {
      navigator.clipboard.writeText(pointsData.referral_code)
        .then(() => { alert('Referral Code Copied!'); })
        .catch(err => { console.error('Failed to copy:', err); });
    }
  };

  const handleCopyLink = () => {
    if (pointsData?.referral_code) {
      navigator.clipboard.writeText('mymax.com/referrals?id=' + pointsData.referral_code + '&n=' + pointsData.first_name)
        .then(() => { alert('Referral Link Copied!'); })
        .catch(err => { console.error('Failed to copy:', err); });
    }
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''));
  };

  const handleCodeSubmit = async () => {
    if (!code) return;
    setCodeLoading(true);
    setCodeResult(null);
    try {
      const result = await submitCodeRedemption({ code, member_number: memberNumber });
      setCodeResult(result);
      if (result.post_result === 'SUCCESS' && result.post_redeemed_pts) {
        setPointsData(prev => ({
          ...prev,
          pts_available_count: (parseFloat(String(prev.pts_available_count).replace(/,/g, '')) + parseFloat(String(result.post_redeemed_pts).replace(/,/g, ''))).toLocaleString(),
        }));
      }
    } catch (err) {
      setCodeResult({ post_result: 'ERROR', post_message: err.message });
    } finally {
      setCodeLoading(false);
    }
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
    setCaseFile(null);
    setCaseResult(null);
  };

  const selectedOptionObj = REQUEST_OPTIONS.find((o) => o.value === selectedOption);

  const caseSubmitEnabled =
    selectedOptionObj &&
    (!selectedOptionObj.requiresUpload || caseFile);

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCaseSubmit = async () => {
    if (!caseSubmitEnabled) return;
    setCaseLoading(true);
    setCaseResult(null);
    try {
      let file;
      if (selectedOptionObj.requiresUpload && caseFile) {
        const data = await readFileAsBase64(caseFile);
        file = { filename: caseFile.name, contentType: caseFile.type, data };
      }
      const result = await submitSalesforceCase({
        memberNumber,
        subject: selectedOptionObj.subject,
        case_type: selectedOptionObj.case_type,
        file,
      });
      setCaseResult({ success: true, message: result.message, caseId: result.caseId });
      setSelectedOption('');
      setCaseFile(null);
    } catch (err) {
      setCaseResult({ success: false, message: err.message });
    } finally {
      setCaseLoading(false);
    }
  };

  if (error) return <div>Error: {error.message}</div>;
  if (!pointsData) return <div className='text-center'>Loading...</div>;

  return (
    <div style={{ display: 'flex' }}>
      <SideMenu menuItems={menuItems} />
      <div style={{ flex: 1 }} className={`${isCollapsed ? 'sb-thin' : 'sb-wide'}`}>
        <Header name="Bonus Points" />
        {isCollapsed && <TopMenu />}
        <div className='sb-body'>
          <div className="mx-auto md:max-w-3xl md:grid md:grid-cols-1 md:gap-4">

            {/* Top tile — available points */}
            <div className="bg-gray-100 p-6 rounded-md mb-2 sm:mb-4">
              <p className="p-redeem text-gray-900 text-lg text-center">You have a total of</p>
              <h2 className="h2-redeem text-gray-900 text-5xl text-center tracking-tighter relative">
                {pointsData.pts_available_count}
              </h2>
              <p className="p-redeem text-gray-900 text-lg text-center">points available</p>
            </div>

            {/* Two-column action tiles */}
            <div className="md:grid md:grid-cols-2 md:gap-4 mb-2 md:mb-4">

              {/* Tile 1 — Use a Code */}
              <div className="bg-green-100 p-6 rounded-md mb-2 sm:mb-4">
                <h2 className="text-h2-referral text-2xl text-center tracking-tight leading-6 font-bold mb-4">
                  Redeem Rewards Code
                </h2>

                <p className="text-xs text-gray-600 mb-2">Have a code? Enter it below and we'll apply your points.</p>

                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  maxLength={20}
                  placeholder="Enter code"
                  className="w-full border border-green-300 rounded-md p-2 text-center text-sm mb-2"
                />

                {code.length > 0 && (
                  <h2 className="text-green-900 text-5xl text-center tracking-tighter font-bold mb-4">
                    {code}
                  </h2>
                )}

                <div className="mt-2">
                  <button
                    onClick={handleCodeSubmit}
                    disabled={code.length === 0 || codeLoading}
                    className="mt-2 inline-block w-full bg-blue-600 border border-transparent rounded-md py-2 text-sm font-bold text-white text-center hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
                  >
                    {codeLoading ? 'Sending...' : 'Submit Code'}
                  </button>
                </div>

                {codeResult && (
                  <div className={`mt-4 p-3 rounded-md text-sm text-center ${codeResult.post_result === 'SUCCESS' ? 'bg-green-200 text-green-900' : 'bg-red-100 text-red-800'}`}>
                    {codeResult.post_result === 'SUCCESS' ? (
                      <p className="font-bold">Success! {codeResult.post_redeemed_pts} points were added to your rewards balance.</p>
                    ) : (
                      <p>{codeResult.post_message || 'Code could not be applied.'}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Tile 2 — Request Points */}
              <div className="bg-blue-100 p-6 rounded-md mb-2 sm:mb-4">
                <h2 className="text-h2-referral text-2xl text-center tracking-tight leading-6 font-bold mb-4">
                  Request Points
                </h2>

                <p className="text-xs text-gray-600 mb-2">Completed an eligible activity? Submit a request for points.</p>

                <select
                  value={selectedOption}
                  onChange={handleOptionChange}
                  className="w-full border border-blue-300 rounded-md p-2 text-sm mb-2"
                >
                  <option value="">Select an activity...</option>
                  {REQUEST_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                {selectedOptionObj?.requiresUpload && (
                  <div className="mt-2 mb-2">
                    <label className="block text-blue-900 text-sm mb-1">
                      Upload supporting document
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setCaseFile(e.target.files[0] || null)}
                      className="w-full text-sm"
                    />
                  </div>
                )}

                <div className="mt-4">
                  <button
                    onClick={handleCaseSubmit}
                    disabled={!caseSubmitEnabled || caseLoading}
                    className={`mt-2 inline-block w-full border border-transparent rounded-md py-2 text-sm font-bold text-white text-center ${
                      !caseSubmitEnabled || caseLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {caseLoading ? 'Sending...' : 'Submit Request'}
                  </button>
                  {selectedOptionObj?.requiresUpload && !caseFile && (
                    <p className="mt-2 text-xs text-amber-700 text-center">Please upload a supporting document before submitting.</p>
                  )}
                </div>

                {caseResult && (
                  <div className={`mt-4 p-3 rounded-md text-sm text-center ${caseResult.success ? 'bg-green-200 text-green-900' : 'bg-red-100 text-red-800'}`}>
                    {caseResult.success ? (
                      <>
                        <p className="font-bold">Request Submitted Successfully</p>
                      </>
                    ) : (
                      <p>{caseResult.message || 'Request could not be submitted.'}</p>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Referral section */}
            <div className="md:grid md:grid-cols-2 md:gap-4">
              <div className="bg-gray-50 p-6 rounded-md mb-2 sm:mb-4">
                <div>
                  <h2 className="text-h2-referral text-2xl text-center tracking-tight leading-6 font-bold mb-4">Share the reward of membership. Refer a friend and get rewarded!</h2>
                  <p className="text-sm text-center"><strong>Referring is easy and starts here!</strong> Earn 5,000 points for both you and your friend. Copy the referral code and share, it's that easy.</p>
                </div>
              </div>

              <div className="bg-blue-100 p-6 rounded-md mb-2 sm:mb-4">
                <div>
                  <p className="p-nomargin text-blue-900 text-sm text-center">Your referral code is</p>
                  <h2 id="displayCode" className="text-refcode text-blue-900 text-5xl text-center tracking-tighter font-bold">{ pointsData.referral_code }</h2>

                  <div className="flex justify-center mb-6">
                    <button
                      onClick={handleCopyCode}
                      className="copy-button mt-2 inline-block w-1/2 bg-blue-600 border border-transparent rounded-full py-1 px-3 text-sm font-bold text-white text-center hover:bg-blue-700"
                    >
                      Copy Code
                    </button>
                  </div>

                  <p className="p-nomargin text-blue-900 text-sm text-center">Your referral link is</p>
                  <h2 id="displayLink" className="text-reflink text-blue-900 text-xl text-center tracking-tighter font-bold">mymax.com/referrals?id={ pointsData.referral_code }&n={ pointsData.first_name }</h2>

                  <div className="flex justify-center">
                    <button
                      onClick={handleCopyLink}
                      className="copy-button mt-2 inline-block w-1/2 bg-blue-600 border border-transparent rounded-full py-1 px-3 text-sm font-bold text-white text-center hover:bg-blue-700"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default GetPoints;
